import { useEffect, useMemo, useState } from "react"
import { useDataStore, useExtras, useQuery } from "../../Data"
import type { IAccountUserInfo } from "../../Types"

interface AccountUserInfoExtras {
  status: IAccountUserInfo["status"];
  hasShipAccess: boolean;
}

export function useAccountUsers(
  accountId: number | undefined,
  enabled: boolean,
  requestVersion: number,
): IAccountUserInfo[] | undefined {
  const store = useDataStore();
  const accountUsers = useQuery(store, "AccountUser", { accountId });
  const users = useQuery(store, "User", { accountId });
  const extras = useExtras(store, "AccountUser", { accountId });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || accountId === undefined) {
      setLoaded(false);
      return;
    }
    let active = true;
    setLoaded(store.isQueryFetched("AccountUser", { accountId }));
    store.fetchQueryStrict("AccountUser", { accountId })
      .then(() => {
        if (active) {
          setLoaded(true);
        }
      })
      .catch(console.error);
    return () => {
      active = false;
    };
  }, [store, accountId, enabled, requestVersion]);

  useEffect(() => {
    if (!enabled || accountId === undefined) {
      return;
    }
    let active = true;
    store.retain("AccountUser");
    store.retain("User");
    const refresh = () => {
      store.fetchQueryStrict("AccountUser", { accountId })
        .then(() => {
          if (active) {
            setLoaded(true);
          }
        })
        .catch(console.error);
    };
    const unsubscribeAccountUser = store.subscribeLive("AccountUser", refresh);
    const unsubscribeUser = store.subscribeLive("User", refresh);
    return () => {
      active = false;
      unsubscribeUser();
      unsubscribeAccountUser();
      store.release("User");
      store.release("AccountUser");
    };
  }, [store, accountId, enabled]);

  return useMemo(() => {
    if (!enabled || !loaded) {
      return undefined;
    }
    const usersById = new Map(users.map((user) => [user.id, user]));
    const infoById = (extras.accountUserInfo ?? {}) as Record<number, AccountUserInfoExtras>;
    return accountUsers.flatMap((accountUser) => {
      const user = usersById.get(accountUser.userId);
      const info = infoById[accountUser.userId];
      if (!user || !info) {
        return [];
      }
      return [{
        level: accountUser.accountLevel,
        status: info.status,
        hasShipAccess: info.hasShipAccess,
        user,
      }];
    });
  }, [enabled, loaded, accountUsers, users, extras]);
}
